import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface Language {
  name: string;
  percentage: number;
}

interface CICDEntry {
  provider: string;
  files: string[];
}

interface ScanResults {
  languages: Language[];
  frameworks: string[];
  testRunners: string[];
  cicd: CICDEntry[];
  coverageHints: string[];
  packageCount: number;
  keyDependencies: string[];
  repoSizeKB: number;
}

interface CodebaseScan {
  id: string;
  projectId: string;
  repoUrl: string;
  scanResults: ScanResults;
  scannedAt: string;
  scannedBy: string | null;
}

interface CodebaseResponse {
  connected: boolean;
  scan: CodebaseScan | null;
}

interface GenerateSummaryResponse {
  artefactId: string;
  overview: string;
  languageCount: number;
  frameworkCount: number;
  riskCount: number;
}

const LANG_COLORS: Record<string, string> = {
  TypeScript: '#3178c6', JavaScript: '#f1e05a', Python: '#3572A5',
  Java: '#b07219', Go: '#00ADD8', Rust: '#dea584', Ruby: '#701516',
  'C#': '#178600', PHP: '#4F5D95', Swift: '#F05138', Kotlin: '#A97BFF',
  HTML: '#e34c26', CSS: '#563d7c', Shell: '#89e051', Dockerfile: '#384d54',
};

export default function ProjectCodebasePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [data, setData] = useState<CodebaseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [repoUrl, setRepoUrl] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [summaryResult, setSummaryResult] = useState<GenerateSummaryResponse | null>(null);
  const [error, setError] = useState('');
  const generateAbort = useRef(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    apiFetch<CodebaseResponse>(`/projects/${projectId}/codebase`)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load codebase'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const handleConnect = async () => {
    if (!repoUrl.trim() || !projectId) return;
    setConnecting(true);
    setError('');
    try {
      const result = await apiFetch<{ connected: boolean; scan: CodebaseScan }>(
        `/projects/${projectId}/codebase/connect`,
        { method: 'POST', body: JSON.stringify({ repoUrl: repoUrl.trim() }) },
      );
      setData({ connected: true, scan: result.scan });
      setRepoUrl('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleRescan = async () => {
    if (!projectId) return;
    setScanning(true);
    setError('');
    try {
      const result = await apiFetch<{ connected: boolean; scan: CodebaseScan }>(
        `/projects/${projectId}/codebase/scan`,
        { method: 'POST' },
      );
      setData({ connected: true, scan: result.scan });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setScanning(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!projectId || generating) return;
    if (generateAbort.current) return;
    generateAbort.current = true;
    setGenerating(true);
    setError('');
    try {
      const result = await apiFetch<GenerateSummaryResponse>(
        `/projects/${projectId}/codebase/generate-summary`,
        { method: 'POST' },
      );
      setSummaryResult(result);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGenerating(false);
      generateAbort.current = false;
    }
  };

  const scan = data?.scan?.scanResults;

  return (
    <div style={{ flex: 1, padding: '32px', maxWidth: 960, color: 'var(--text)' }}>
        <div style={{ marginBottom: 8 }}>
          <Link to={`/projects/${projectId}`} style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>
            &larr; Back to project
          </Link>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Codebase Intelligence</h1>

        {error && (
          <div style={{ padding: '8px 16px', background: '#fef2f2', color: '#dc2626', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}

        {loading && <div style={{ color: 'var(--text-muted)' }}>Loading...</div>}

        {/* Connect form — show when not connected and no fetch error */}
        {!loading && !data?.connected && !error && (
          <div style={{ background: 'var(--surface-alt)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Connect GitHub Repository</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Connect a GitHub repository to scan its tech stack, test coverage configuration, and CI/CD setup.
              The scan results will be used to provide context-aware architecture recommendations.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="https://github.com/owner/repo"
                value={repoUrl}
                onChange={e => setRepoUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConnect()}
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg)',
                  color: 'var(--text)', fontSize: 14,
                }}
              />
              <button
                onClick={handleConnect}
                disabled={connecting || !repoUrl.trim()}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: 'var(--accent)', color: '#fff', cursor: 'pointer',
                  fontWeight: 600, fontSize: 14,
                  opacity: connecting || !repoUrl.trim() ? 0.5 : 1,
                }}
              >
                {connecting ? 'Connecting...' : 'Connect & Scan'}
              </button>
            </div>
          </div>
        )}

        {/* Scan results */}
        {!loading && data?.connected && scan && (
          <>
            {/* Header with repo URL + rescan */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'var(--surface-alt)', borderRadius: 12, padding: '16px 20px', marginBottom: 20,
            }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 2 }}>Connected repository</div>
                <a
                  href={data.scan!.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 15, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}
                >
                  {data.scan!.repoUrl.replace('https://github.com/', '')}
                </a>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  Last scanned: {new Date(data.scan!.scannedAt).toLocaleString()}
                </div>
              </div>
              <button
                onClick={handleRescan}
                disabled={scanning}
                style={{
                  padding: '6px 14px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer', fontSize: 13,
                  opacity: scanning ? 0.5 : 1,
                }}
              >
                {scanning ? 'Scanning...' : 'Re-scan'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {/* Languages */}
              <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Languages</h3>
                {scan.languages.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No language data</div>}
                {scan.languages.map(l => (
                  <div key={l.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: LANG_COLORS[l.name] ?? '#888', flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 13, flex: 1 }}>{l.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.percentage}%</span>
                    <div style={{
                      width: 60, height: 4, borderRadius: 2, background: 'var(--border)',
                    }}>
                      <div style={{
                        width: `${l.percentage}%`, height: '100%', borderRadius: 2,
                        background: LANG_COLORS[l.name] ?? '#888',
                      }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Frameworks */}
              <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Frameworks & Tools</h3>
                {scan.frameworks.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No frameworks detected</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {scan.frameworks.map(f => (
                    <span key={f} style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                      fontSize: 12, fontWeight: 500,
                      background: 'var(--accent-muted, rgba(99,102,241,0.12))', color: 'var(--accent)',
                    }}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {/* Test Runners */}
              <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Testing</h3>
                {scan.testRunners.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No test runner detected</div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {scan.testRunners.map(t => (
                        <span key={t} style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                          fontSize: 12, fontWeight: 500,
                          background: 'rgba(16,185,129,0.12)', color: '#10b981',
                        }}>
                          {t}
                        </span>
                      ))}
                    </div>
                    {scan.coverageHints.length > 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Coverage config: {scan.coverageHints.join(', ')}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* CI/CD */}
              <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>CI/CD</h3>
                {scan.cicd.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No CI/CD configuration detected</div>
                ) : (
                  scan.cicd.map(c => (
                    <div key={c.provider} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{c.provider}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {c.files.join(', ')}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Key Dependencies */}
            {scan.keyDependencies.length > 0 && (
              <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                  Key Dependencies ({scan.packageCount} total)
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {scan.keyDependencies.map(d => (
                    <span key={d} style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                      fontSize: 12, fontWeight: 500, border: '1px solid var(--border)',
                      color: 'var(--text-muted)',
                    }}>
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Generate Codebase Summary */}
            <div style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Codebase Summary Artefact</h3>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                    Generate an AI-powered summary with architecture assessment, risk analysis, and recommendations.
                  </p>
                </div>
                <button
                  onClick={handleGenerateSummary}
                  disabled={generating}
                  style={{
                    padding: '8px 16px', borderRadius: 6, border: 'none',
                    background: 'var(--accent)', color: '#fff', cursor: 'pointer',
                    fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
                    opacity: generating ? 0.5 : 1,
                  }}
                >
                  {generating ? 'Generating...' : 'Generate Summary'}
                </button>
              </div>

              {summaryResult && (
                <div style={{
                  marginTop: 12, padding: 12, borderRadius: 8,
                  background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981', marginBottom: 4 }}>
                    Summary generated
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>
                    {summaryResult.overview}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-muted)' }}>
                    <span>{summaryResult.languageCount} languages</span>
                    <span>{summaryResult.frameworkCount} frameworks</span>
                    <span>{summaryResult.riskCount} risks identified</span>
                  </div>
                  <Link
                    to={`/artefacts?tabs=${summaryResult.artefactId}&active=${summaryResult.artefactId}`}
                    style={{
                      display: 'inline-block', marginTop: 8, fontSize: 13,
                      color: 'var(--accent)', textDecoration: 'none', fontWeight: 500,
                    }}
                  >
                    View full summary &rarr;
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
    </div>
  );
}
